import { FONT_STACK } from "./theme/modernTheme.js";

/** Çinko nötr ölçek + Slate ikincil — ana vurgu primary ile MUI palette'de override edilir */
export const tokensDark = {
  grey: {
    0: "#fafafa",
    10: "#f4f4f5",
    50: "#e4e4e7",
    100: "#d4d4d8",
    200: "#a1a1aa",
    300: "#71717a",
    400: "#52525b",
    500: "#3f3f46",
    600: "#27272a",
    700: "#1f1f23",
    800: "#18181b",
    900: "#0c0c0e",
    1000: "#030303",
  },
  primary: {
    100: "#f5f5f5",
    200: "#e5e5e5",
    300: "#d4d4d4",
    400: "#fafafa",
    500: "#a3a3a3",
    600: "#737373",
    700: "#525252",
    800: "#404040",
    900: "#262626",
  },
  secondary: {
    100: "#f1f5f9",
    200: "#e2e8f0",
    300: "#cbd5e1",
    400: "#94a3b8",
    500: "#64748b",
    600: "#475569",
    700: "#334155",
    800: "#1e293b",
    900: "#0f172a",
  },
  accent: {
    100: "#f5f5f5",
    200: "#e5e5e5",
    300: "#d4d4d4",
    400: "#a3a3a3",
    500: "#737373",
    600: "#525252",
    700: "#404040",
    800: "#262626",
    900: "#171717",
  },
};

function reverseTokens(tokensDark) {
  const reversedTokens = {};
  Object.entries(tokensDark).forEach(([key, val]) => {
    const keys = Object.keys(val);
    const values = Object.values(val);
    const length = keys.length;
    const reversedObj = {};
    for (let i = 0; i < length; i++) {
      reversedObj[keys[i]] = values[length - i - 1];
    }
    reversedTokens[key] = reversedObj;
  });
  return reversedTokens;
}

export const tokensLight = reverseTokens(tokensDark);

const semantic = {
  success: "#22c55e",
  warning: "#eab308",
  error: "#ef4444",
};

const compactTypography = (mode) => {
  const isDark = mode === "dark";
  const textPrimary = isDark ? tokensDark.grey[10] : tokensDark.grey[900];
  const textSecondary = isDark ? tokensDark.grey[200] : tokensDark.grey[400];
  const headingAccent = isDark ? "#fafafa" : "#0a0a0a";

  return {
    fontFamily: FONT_STACK,
    fontSize: 13,
    h1: {
      fontFamily: FONT_STACK,
      fontSize: "1.375rem",
      fontWeight: 600,
      letterSpacing: "-0.03em",
      color: isDark ? textPrimary : tokensDark.grey[900],
    },
    h2: {
      fontFamily: FONT_STACK,
      fontSize: "1.125rem",
      fontWeight: 600,
      letterSpacing: "-0.02em",
      color: headingAccent,
    },
    h3: {
      fontFamily: FONT_STACK,
      fontSize: "1rem",
      fontWeight: 600,
      letterSpacing: "-0.015em",
      color: headingAccent,
    },
    h4: {
      fontFamily: FONT_STACK,
      fontSize: "0.9375rem",
      fontWeight: 600,
      letterSpacing: "-0.01em",
      color: isDark ? tokensDark.grey[10] : tokensDark.grey[900],
    },
    h5: {
      fontFamily: FONT_STACK,
      fontSize: "0.875rem",
      fontWeight: 600,
      letterSpacing: "-0.01em",
      color: textPrimary,
    },
    h6: {
      fontFamily: FONT_STACK,
      fontSize: "0.8125rem",
      fontWeight: 500,
      letterSpacing: "0",
      color: textSecondary,
    },
    body1: {
      fontFamily: FONT_STACK,
      fontSize: "0.8125rem",
      color: textSecondary,
      lineHeight: 1.55,
      letterSpacing: "0.01em",
    },
    body2: {
      fontFamily: FONT_STACK,
      fontSize: "0.75rem",
      color: isDark ? tokensDark.grey[300] : tokensDark.grey[500],
      lineHeight: 1.45,
      letterSpacing: "0.01em",
    },
  };
};

const darkComponentOverrides = {
  MuiPaper: {
    styleOverrides: {
      root: {
        backgroundColor: "rgba(255, 255, 255, 0.03)",
        backdropFilter: "blur(8px)",
        border: "1px solid rgba(255, 255, 255, 0.1)",
        borderRadius: 8,
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        backgroundColor: "rgba(255, 255, 255, 0.03)",
        backdropFilter: "blur(8px)",
        border: "1px solid rgba(255, 255, 255, 0.1)",
        borderRadius: 8,
        transition: "border-color 0.2s ease, box-shadow 0.2s ease",
        "&:hover": {
          transform: "none",
          boxShadow: "0 4px 14px rgba(0, 0, 0, 0.35)",
          borderColor: "rgba(255, 255, 255, 0.2)",
        },
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: {
        textTransform: "none",
        borderRadius: 6,
        fontWeight: 500,
        fontSize: "0.8125rem",
        letterSpacing: "0.01em",
        padding: "6px 14px",
        minHeight: 32,
        transition: "background-color 0.15s ease, box-shadow 0.15s ease",
        "&:hover": {
          transform: "none",
          boxShadow: "0 2px 8px rgba(255, 255, 255, 0.08)",
        },
      },
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        "& .MuiOutlinedInput-root": {
          backgroundColor: "rgba(255, 255, 255, 0.04)",
          backdropFilter: "blur(6px)",
          borderRadius: 6,
          fontSize: "0.8125rem",
          "& fieldset": {
            borderColor: "rgba(255, 255, 255, 0.12)",
          },
          "&:hover fieldset": {
            borderColor: "rgba(255, 255, 255, 0.28)",
          },
          "&.Mui-focused fieldset": {
            borderColor: "#fafafa",
            borderWidth: 1,
          },
        },
        "& .MuiInputLabel-root": {
          fontSize: "0.8125rem",
          color: "#a1a1aa",
          "&.Mui-focused": {
            color: "#fafafa",
          },
        },
        "& .MuiInputBase-input": {
          color: "#fafafa",
        },
      },
    },
  },
};

const lightComponentOverrides = {
  MuiPaper: {
    styleOverrides: {
      root: {
        backgroundColor: "#ffffff",
        border: "1px solid rgba(10, 10, 10, 0.1)",
        borderRadius: 8,
        boxShadow: "0 1px 0 rgba(10, 10, 10, 0.04)",
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        backgroundColor: "#ffffff",
        border: "1px solid rgba(10, 10, 10, 0.1)",
        borderRadius: 8,
        boxShadow: "0 1px 2px rgba(10, 10, 10, 0.04)",
        transition: "box-shadow 0.2s ease, border-color 0.2s ease",
        "&:hover": {
          transform: "none",
          boxShadow: "0 4px 12px rgba(10, 10, 10, 0.08)",
          borderColor: "rgba(10, 10, 10, 0.18)",
        },
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: {
        textTransform: "none",
        borderRadius: 6,
        fontWeight: 500,
        fontSize: "0.8125rem",
        letterSpacing: "0.01em",
        padding: "6px 14px",
        minHeight: 32,
        "&:hover": {
          transform: "none",
          boxShadow: "0 2px 8px rgba(10, 10, 10, 0.1)",
        },
      },
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        "& .MuiOutlinedInput-root": {
          backgroundColor: "#ffffff",
          borderRadius: 6,
          fontSize: "0.8125rem",
          "& fieldset": {
            borderColor: "rgba(10, 10, 10, 0.14)",
          },
          "&:hover fieldset": {
            borderColor: "rgba(10, 10, 10, 0.28)",
          },
          "&.Mui-focused fieldset": {
            borderColor: "#0a0a0a",
            borderWidth: 1,
          },
        },
        "& .MuiInputLabel-root": {
          fontSize: "0.8125rem",
          color: "#52525b",
          "&.Mui-focused": {
            color: "#0a0a0a",
          },
        },
        "& .MuiInputBase-input": {
          color: "#0a0a0a",
        },
      },
    },
  },
};

export const themeSettings = (mode) => {
  const isDark = mode === "dark";

  return {
    palette: {
      mode,
      ...(isDark
        ? {
            primary: {
              ...tokensDark.primary,
              main: "#fafafa",
              light: "#ffffff",
              dark: "#e5e5e5",
              contrastText: "#0a0a0a",
            },
            secondary: {
              ...tokensDark.secondary,
              main: tokensDark.secondary[400],
            },
            neutral: {
              ...tokensDark.grey,
              main: tokensDark.grey[400],
            },
            background: {
              default: "#050505",
              alt: "#0a0a0a",
              paper: "#0f0f0f",
            },
            text: {
              primary: "#fafafa",
              secondary: "#a3a3a3",
            },
            success: { main: semantic.success },
            warning: { main: semantic.warning },
            error: { main: semantic.error },
          }
        : {
            primary: {
              ...tokensLight.primary,
              main: "#0a0a0a",
              light: "#262626",
              dark: "#000000",
              contrastText: "#fafafa",
            },
            secondary: {
              ...tokensLight.secondary,
              main: tokensDark.secondary[600],
            },
            neutral: {
              ...tokensLight.grey,
              main: tokensLight.grey[500],
            },
            background: {
              default: "#fafafa",
              alt: "#f5f5f5",
              paper: "#ffffff",
            },
            text: {
              primary: "#0a0a0a",
              secondary: "#525252",
            },
            success: { main: "#16a34a" },
            warning: { main: "#ca8a04" },
            error: { main: "#dc2626" },
          }),
    },
    typography: compactTypography(mode),
    components: isDark ? darkComponentOverrides : lightComponentOverrides,
  };
};
